import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import { AdvanceBreadcrumb } from '@/components/Breadcrumb';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Descriptions, Form, Input, message, Modal, Tabs, Typography } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, SyncOutlined, EditOutlined } from '@ant-design/icons';
import { getDeviceDetail, getProductInfo } from '@/api';
import { deviceSyncSingle, editDevices, IDeviceDetail } from '@/api/device';
import { getDeptDetail } from '@/api/system/dept';
import { DetailTab1 } from './components/Tab1';
import { DetailTab2 } from './components/Tab2';
import { DetailTab3 } from './components/Tab3';
import Tab4 from './components/Tab4';
import './style.less';
import dayjs from 'dayjs';
import { ProductInfo } from './types';
import Tab5 from './components/Tab5';
import useAuthButton from '@/hooks/useAuthButton';
import { Alarm, Channel, StreamPlay } from './components/Ipc';
import MapFormItems from './components/MapFormItems';

const { Text, Link } = Typography;

export interface DeviceDetailProps {}
interface EditorModalRef {
  open: (data: { code: string; label: string; value?: string; detail?: IDeviceDetail }) => void;
}
const EditorModal = forwardRef<EditorModalRef, { onSuccess: () => void }>(({ onSuccess }, ref) => {
  const params = useParams<{ id: string }>();
  const [data, setData] = useState<{ visible: boolean; code?: string; label?: string; value?: string }>({ visible: false });
  const [form] = Form.useForm();
  useImperativeHandle(ref, () => ({
    open: ({ code, label, value, detail }) => {
      console.log({ code, label, value });
      form.setFieldsValue({ ...detail, [code]: value });
      setData({ code, label, value, visible: true });
    },
  }));

  const { run, loading } = useRequest(editDevices, {
    manual: true,
    onSuccess: () => {
      setData({ visible: false });
      onSuccess();
    },
  });

  return (
    <Modal
      title="编辑"
      open={data.visible}
      okButtonProps={{ loading }}
      onCancel={() => setData({ visible: false })}
      okText="保存"
      cancelText="取消"
      onOk={() => {
        const toData = form.getFieldsValue();
        run({ ...params, ...toData, ...(toData.lon ? { lon: String(toData.lon), lat: String(toData.lat) } : {}) });
      }}
    >
      <Form layout="horizontal" form={form} className="mt10">
        {data.code === 'location' ? (
          <MapFormItems isOnlyMap></MapFormItems>
        ) : (
          <Form.Item name={data.code} label={data.label} required={data.code === 'name'} rules={[{ required: data.code === 'name', message: `请输入${data.label}` }]}>
            <Input placeholder={`请输入${data.label}`} allowClear></Input>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
});

function EditorIcon({ code, modalRef, label, detail }: { code: keyof IDeviceDetail; modalRef: React.MutableRefObject<EditorModalRef | null>; label: string; detail?: IDeviceDetail }) {
  return (
    <EditOutlined
      className="ml10"
      style={{ cursor: 'pointer' }}
      onClick={() => {
        modalRef.current?.open({ code, label, value: detail?.[code] as string, detail });
      }}
    ></EditOutlined>
  );
}

export const DeviceDetail: React.FC<DeviceDetailProps> = () => {
  const [searchParams] = useSearchParams();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { checkPermissionDisabled } = useAuthButton();

  const [activeKey, setActiveKey] = useState('1');
  const [detail, setDetail] = useState<IDeviceDetail>();
  const [enterpriseName, setEnterpriseName] = useState<string>();
  const [visible, setVisible] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);

  const isCloudDevice = useMemo(() => detail?.cloud_device_id, [detail]);

  useEffect(() => {
    const trigger = decodeURIComponent(searchParams.get('trigger') || '');
    const mapping: { [x: string]: string } = {
      设备数据触发: '1',
      设备事件触发: '2',
      子设备触发: '4',
    };
    trigger && mapping[trigger] && setActiveKey(mapping[trigger]);
  }, []);

  const loadDetail = useCallback(() => {
    getDeviceDetail(params.id!).then((resp) => {
      if (resp.success) {
        setDetail(resp.result);
        loadProductInfo(resp.result);
      }
    });
  }, [params]);

  useEffect(() => {
    loadDetail();

    localStorage.setItem('title', '设备详情');
    document.title = '设备详情';
  }, []);

  useEffect(() => {
    const deptId = detail?.enterprise_id;
    if (!deptId) {
      setEnterpriseName(undefined);
      return;
    }

    setEnterpriseName(undefined);
    getDeptDetail({ id: deptId })
      .then((resp: any) => {
        const deptName = resp?.success ? resp?.result?.dept_name : undefined;
        setEnterpriseName(deptName || undefined);
      })
      .catch(() => {
        setEnterpriseName(undefined);
      });
  }, [detail?.enterprise_id]);

  const [productInfo, setProductInfo] = useState<ProductInfo>();
  const loadProductInfo = useCallback(
    (detail?: IDeviceDetail) => {
      getProductInfo<ProductInfo>(detail?.product_id!).then((resp) => {
        if (resp.success) {
          setProductInfo(resp.result);
        }
      });
    },
    [params],
  );

  const modalRef = useRef<EditorModalRef>(null);

  return (
    <>
      <AdvanceBreadcrumb
        hasBack
        title={
          <div className="flex-center">
            <Text style={{ fontSize: 17 }} copyable>
              {detail?.name || '设备详情'}
            </Text>
          </div>
        }
        describe="设备详情提供设备基础信息及物模型相关数据（包括设备属性功能记录、事件记录及服务调用记录）查询。"
        background={require('@/assets/images/head-bg.8b029587.png')}
        breadcrumb={[
          { label: '设备管理', to: '/gateway/device/manage' },
          { label: `设备详情 [ ${detail?.name || ''} ]`, to: '' },
        ]}
      />
      <EditorModal
        ref={modalRef}
        onSuccess={() => {
          loadDetail();
        }}
      ></EditorModal>
      <Card className="product-info">
        <Descriptions
          className="product-info__descriptions"
          title={'设备信息'}
          column={3}
          size="small"
          bordered
          labelStyle={{ fontSize: 12, color: '#8a949c' }}
          contentStyle={{ fontSize: 12 }}
          extra={
            isCloudDevice && (
              <Button
                type="link"
                size="small"
                loading={submitting}
                disabled={submitting}
                icon={<SyncOutlined />}
                style={{ fontSize: 12 }}
                onClick={() => {
                  setSubmitting(true);
                  deviceSyncSingle({ device_id: params.id! })
                    .then((resp) => {
                      if (resp.success) {
                        message.success('同步成功');
                        loadDetail();
                      } else {
                        message.error(resp.errorMsg);
                      }
                    })
                    .finally(() => {
                      setSubmitting(false);
                    });
                }}
              >
                同步设备
              </Button>
            )
          }
        >
          <Descriptions.Item label="设备名称">
            {detail?.name}
            <EditorIcon code="name" modalRef={modalRef} label="设备名称" detail={detail}></EditorIcon>
          </Descriptions.Item>
          <Descriptions.Item label="设备ID">
            <Text style={{ fontSize: 12 }} copyable>
              {detail?.id}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="设备位置">
            {detail?.location}
            <EditorIcon code="location" modalRef={modalRef} label="设备位置" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="设备状态">
            <Badge
              color={
                {
                  在线: 'green',
                  离线: 'gray',
                  禁用: 'red',
                  未激活: 'orange',
                }[detail?.status as string] || 'gray'
              }
            />
            <Text className="ml5" style={{ fontSize: 12 }}>
              {detail?.status || '未知'}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="设备密钥">
            {detail?.secret && (
              <>
                {visible ? (
                  <>
                    <Text style={{ fontSize: 12 }} className="mr10" copyable>
                      {detail?.secret}
                    </Text>
                    <EyeInvisibleOutlined onClick={() => setVisible(!visible)} />
                  </>
                ) : (
                  <>
                    <span className="mr10">{new Array(detail?.secret.length).fill(null).map(() => '*')}</span>
                    <EyeOutlined onClick={() => setVisible(!visible)} />
                  </>
                )}
              </>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="关联驱动">
            {detail?.device_service_name || '-'}({detail?.status})
          </Descriptions.Item>
          <Descriptions.Item label="所属产品">
            <Link
              style={{ fontSize: 12 }}
              onClick={() => {
                navigate(`/gateway/product/detail/${detail?.product_id}`);
              }}
            >
              {detail?.product_name}
            </Link>
          </Descriptions.Item>

          <Descriptions.Item label="设备唯一标识">
            {detail?.device_sn}
            <EditorIcon code="device_sn" modalRef={modalRef} label="设备唯一标识" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="设备厂商">
            {detail?.manufacturer}
            <EditorIcon code="manufacturer" modalRef={modalRef} label="设备厂商" detail={detail}></EditorIcon>
          </Descriptions.Item>
          <Descriptions.Item label="设备IP地址">
            {detail?.ip}
            <EditorIcon code="ip" modalRef={modalRef} label="设备IP地址" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="Modbus从设备地址">
            {detail?.slaveId}
            <EditorIcon code="slaveId" modalRef={modalRef} label="Modbus从设备地址" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="采集周期(s)">
            {detail?.period}
            <EditorIcon code="period" modalRef={modalRef} label="采集周期" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="设备端口">
            {detail?.port}
            <EditorIcon code="port" modalRef={modalRef} label="设备端口" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="型号">
            {detail?.model}
            <EditorIcon code="model" modalRef={modalRef} label="型号" detail={detail}></EditorIcon>
          </Descriptions.Item>

          <Descriptions.Item label="固件版本">
            {detail?.firmware || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="制造编号">
            {detail?.manufacture_no || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="设备地址">
            {detail?.device_address || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="所属企业">
            {enterpriseName || detail?.enterprise_id || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="所属用户">
            {detail?.user_id || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="是否为视频设备">{detail?.is_ipc ? '是' : '否'}</Descriptions.Item>
          <Descriptions.Item label="设备类型">
            {detail?.device_type}
            {!detail?.child_device_count ? '' : <>({detail?.child_device_count}个子设备)</>}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{detail?.create_at ? dayjs(detail?.create_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{!detail?.modified_at ? '-' : dayjs(detail?.modified_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
          {/* <Descriptions.Item label="平台">{detail?.platform}</Descriptions.Item>
          <Descriptions.Item label="关联驱动名称">{detail?.device_service_name}</Descriptions.Item> */}
          {/* {isCloudDevice && (
            <Descriptions.Item label="云端设备标识" span={2}>
              <Text style={{ fontSize: 12 }} copyable>
                {detail?.cloud_device_id}
              </Text>
            </Descriptions.Item>
          )} */}
          {/* <Descriptions.Item label="最近在线时间">{detail?.last_online_time ? dayjs(detail?.last_online_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item> */}
          {/* {isCloudDevice && <Descriptions.Item label="最后一次同步时间">{detail?.last_sync_time ? dayjs(detail?.last_sync_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>} */}
          <Descriptions.Item label="描述" span={3}>
            {detail?.description}
            <EditorIcon code="description" modalRef={modalRef} label="描述" detail={detail}></EditorIcon>
          </Descriptions.Item>
        </Descriptions>
      </Card>
      {!detail?.is_ipc ? (
        <Tabs
          className="device-property mt20"
          type="card"
          activeKey={activeKey}
          onChange={(activeKey) => setActiveKey(activeKey)}
          items={[
            {
              key: '1',
              label: '属性',
              children: <DetailTab1 product_id={productInfo?.id}></DetailTab1>,
            },
            {
              key: '2',
              label: '事件记录',
              children: <DetailTab2 events={productInfo?.events || []}></DetailTab2>,
            },
            {
              key: '3',
              label: '服务记录',
              children: <DetailTab3 actions={productInfo?.actions || []}></DetailTab3>,
            },
            ...(productInfo?.node_type === '网关设备' && !checkPermissionDisabled('gateway:device:subdevice')
              ? [
                  {
                    key: '4',
                    label: '子设备',
                    children: <Tab4 id={params.id!}></Tab4>,
                  },
                ]
              : []),
            {
              key: '5',
              label: '设备调试',
              children: <Tab5 detail={detail} productInfo={productInfo} loadDetail={loadDetail}></Tab5>,
            },
          ]}
        />
      ) : (
        <Tabs
          className="device-property mt20"
          type="card"
          activeKey={activeKey}
          onChange={(activeKey) => setActiveKey(activeKey)}
          items={[
            { key: '1', label: '设备通道', children: <Channel device_id={params.id!}></Channel> },
            { key: '2', label: '设备直播', children: <StreamPlay device_id={params.id!}></StreamPlay> },
            { key: '3', label: '设备告警', children: <Alarm device_id={params.id!}></Alarm> },
          ]}
        />
      )}
    </>
  );
};

export default DeviceDetail;
